import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Handshake, Users, Radio, Mic, Trophy, CheckCircle, ArrowRight, Sparkles, Send, Mail,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";

const INTERESTS = [
  { key: "radio_rotation", label: "Radio ad rotation" },
  { key: "hero_banner", label: "Hero banner on TV + web" },
  { key: "tv_app", label: "Smart TV app placement" },
  { key: "socials", label: "Tap IN TV socials posts" },
  { key: "contest", label: "Co-branded contest / giveaway" },
  { key: "custom", label: "Custom creative partnership" },
];

/**
 * SponsorsPage — public sponsorship landing + inquiry form.
 * Pulls tiers + audience stats from /api/sponsorship/info (admin-editable
 * in a future pass). Submitting the form creates a `sponsorship_inquiries`
 * row that the admin reviews in Admin → Sponsorship.
 */
export default function SponsorsPage() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    company: "", contact_name: "", contact_email: "", contact_phone: "",
    website: "", budget_tier: "growth", interests: [], message: "",
  });

  useEffect(() => {
    api.get("/sponsorship/info")
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ tiers: [], stats: {}, hero_blurb: "", contact_email: "sponsor@tapintv.com" }))
      .finally(() => setLoading(false));
  }, []);

  const toggleInterest = (key) => setForm((f) => ({
    ...f,
    interests: f.interests.includes(key)
      ? f.interests.filter((k) => k !== key)
      : [...f.interests, key],
  }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.company.trim() || !form.contact_name.trim() || !form.contact_email.trim()) {
      toast.error("Company, contact name and email are required"); return;
    }
    setSubmitting(true);
    try {
      await api.post("/sponsorship/inquiry", form);
      setSubmitted(true);
      toast.success("Inquiry received — our team will reach out within 2 business days.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (loading || !info) return <div className="min-h-screen bg-[#05060a] text-white flex items-center justify-center">Loading…</div>;

  const stats = info.stats || {};
  const statTiles = [
    { icon: Users,  value: formatK(stats.monthly_listeners), label: "Monthly listeners" },
    { icon: Radio,  value: stats.live_shows_per_month,      label: "Live shows / month" },
    { icon: Mic,    value: stats.creators_on_network,       label: "Creators on network" },
    { icon: Sparkles, value: formatK(stats.socials_followers), label: "Socials followers" },
  ];

  return (
    <div data-testid="sponsors-page" className="min-h-screen bg-[#05060a] text-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-500/20 via-fuchsia-600/10 to-tv-accent/10 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 pt-28 md:pt-32 pb-14">
          <div className="inline-flex items-center gap-2 bg-tv-accent/20 border border-tv-accent/40 text-tv-accent text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4">
            <Handshake className="w-3 h-3" /> Partner with Tap IN TV
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl leading-tight">
            Put your brand in front of <span className="text-tv-accent">independent creators</span> — and the audience that lives for them.
          </h1>
          <p className="text-white/70 mt-4 max-w-3xl text-lg">{info.hero_blurb}</p>
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <a
              href="#inquire"
              data-testid="sponsors-cta-top"
              className="bg-tv-accent hover:bg-yellow-400 text-black font-black text-lg px-6 py-3 rounded-full inline-flex items-center gap-2"
            >
              Become a sponsor <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={`mailto:${info.contact_email}`}
              className="bg-white/10 hover:bg-white/20 text-white font-black px-5 py-3 rounded-full inline-flex items-center gap-2"
            >
              <Mail className="w-4 h-4" /> {info.contact_email}
            </a>
          </div>
        </div>
      </section>

      {/* Audience tiles */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {statTiles.map((s, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4">
              <s.icon className="w-5 h-5 text-tv-accent mb-2" />
              <div className="font-display font-black text-3xl">{s.value ?? "—"}</div>
              <div className="text-white/60 text-xs mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
        {stats.top_genres?.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-white/60 text-sm">
            <span className="uppercase tracking-widest text-[10px] font-black text-white/40">Top genres</span>
            {stats.top_genres.map((g) => (
              <span key={g} className="bg-white/5 border border-white/10 px-2 py-0.5 rounded-full text-xs">{g}</span>
            ))}
            {stats.us_audience_pct != null && (
              <span className="ml-auto text-white/50 text-xs">{stats.us_audience_pct}% US audience</span>
            )}
          </div>
        )}
      </section>

      {/* Tiers */}
      <section className="max-w-6xl mx-auto px-6 pb-10">
        <h2 className="font-display font-black text-2xl mb-4">Packages</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(info.tiers || []).map((t) => (
            <div
              key={t.key}
              data-testid={`sponsor-tier-${t.key}`}
              className={`rounded-xl p-5 border ${
                t.key === "growth"
                  ? "bg-gradient-to-br from-fuchsia-500/20 to-tv-accent/10 border-fuchsia-500/40"
                  : "bg-white/5 border-white/10"
              }`}
            >
              {t.key === "growth" && (
                <div className="inline-block bg-fuchsia-500 text-black text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded mb-2">
                  Most popular
                </div>
              )}
              <h3 className="font-display font-black text-2xl">{t.name}</h3>
              <div className="mt-1 text-white/90">
                <span className="font-display font-black text-3xl">${Number(t.price_usd || 0).toLocaleString()}</span>
                <span className="text-white/50 text-sm"> / {t.cadence || "month"}</span>
              </div>
              <ul className="mt-3 space-y-1.5">
                {(t.features || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                    <CheckCircle className="w-4 h-4 text-tv-accent shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => {
                  setForm((fm) => ({ ...fm, budget_tier: t.key }));
                  document.getElementById("inquire")?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid={`sponsor-tier-pick-${t.key}`}
                className="mt-4 w-full bg-tv-accent hover:bg-yellow-400 text-black font-black text-sm py-2.5 rounded"
              >Pick {t.name} →</button>
            </div>
          ))}
        </div>
      </section>

      {/* Inquiry form */}
      <section id="inquire" className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-5 h-5 text-tv-accent" />
            <h2 className="font-display font-black text-2xl">Tell us what you're building</h2>
          </div>
          <p className="text-white/60 text-sm mb-5">We typically respond within 2 business days. Prefer email? <a className="underline" href={`mailto:${info.contact_email}`}>{info.contact_email}</a>.</p>

          {submitted ? (
            <div data-testid="sponsor-thankyou" className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-6 text-center">
              <CheckCircle className="w-8 h-8 text-emerald-300 mx-auto mb-2" />
              <div className="font-display font-black text-xl">Got it — talk soon.</div>
              <div className="text-white/70 text-sm mt-1">Our sponsorship team will review your inquiry and follow up at <strong>{form.contact_email}</strong>.</div>
              <button
                onClick={() => navigate("/")}
                className="mt-4 bg-white/10 hover:bg-white/20 font-black text-sm px-5 py-2 rounded"
              >Back to Tap IN TV</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="sponsor-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Company *">
                  <input data-testid="sponsor-company" required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Website">
                  <input data-testid="sponsor-website" placeholder="https://…" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Your name *">
                  <input data-testid="sponsor-name" required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Email *">
                  <input data-testid="sponsor-email" type="email" required value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Phone">
                  <input data-testid="sponsor-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Budget tier *">
                  <select data-testid="sponsor-budget" value={form.budget_tier} onChange={(e) => setForm({ ...form, budget_tier: e.target.value })} className={inputCls}>
                    {(info.tiers || []).map((t) => (
                      <option key={t.key} value={t.key}>{t.name} · ${Number(t.price_usd || 0).toLocaleString()}/{t.cadence || "mo"}</option>
                    ))}
                    <option value="custom">Custom / let's talk</option>
                  </select>
                </Field>
              </div>

              <Field label="What are you interested in?">
                <div className="flex flex-wrap gap-1.5" data-testid="sponsor-interests">
                  {INTERESTS.map((i) => (
                    <button
                      type="button"
                      key={i.key}
                      onClick={() => toggleInterest(i.key)}
                      data-testid={`sponsor-interest-${i.key}`}
                      className={`text-xs font-bold px-2.5 py-1 rounded-full border transition ${
                        form.interests.includes(i.key)
                          ? "bg-tv-accent text-black border-tv-accent"
                          : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
                      }`}
                    >{i.label}</button>
                  ))}
                </div>
              </Field>

              <Field label="Tell us more">
                <textarea
                  data-testid="sponsor-message"
                  rows={5} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Campaign timing, creative concept, target audience — anything you'd like us to know."
                  className={inputCls}
                />
              </Field>

              <button
                type="submit" disabled={submitting}
                data-testid="sponsor-submit"
                className="w-full bg-tv-accent hover:bg-yellow-400 disabled:opacity-40 text-black font-black text-lg py-3 rounded inline-flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send inquiry"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white focus:border-tv-accent outline-none";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider font-black text-white/60 mb-1">{label}</div>
      {children}
    </label>
  );
}

function formatK(n) {
  if (n == null) return "—";
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return String(n);
}
